import {
  buildPath,
  buildTemplatePath,
  CloseButton,
  formatDate,
  ListContext,
  ListContextValue,
  newToast,
  Page,
  PageRoute,
  pn,
  Stack,
  Typography,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import { BulkSelected, SubmitButton } from "components";
import {
  BulkSecondLevelValidation,
  BulkSelectedItemErrors,
  StudentService,
  StudentServiceBulkUpdate,
  StudentServiceBulkUpdateSchema,
  StudentServiceEditResource,
} from "generated/openApi";
import { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import ApiService from "services/apiService";
import { ServiceUpdateSchema } from "./ServiceUpdateSchema/ServiceUpdateSchema";

const routeName = "studentServiceBulkUpdate";

export const studentServiceBulkUpdatePageRoute: PageRoute = {
  handle: "Batch Update Services",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export type StudentServiceBulkUpdatePageState = {
  selectedIds: number[];
};

export const StudentServiceBulkUpdatePage = () => {
  const dispatch = useDispatch();
  const listContext = useContext<ListContextValue>(ListContext);
  const { state } = useLocation();
  const [stateParam] = useState<StudentServiceBulkUpdatePageState>(state);
  const [bulkUpdate, setBulkUpdate] = useState<StudentServiceBulkUpdate>({} as StudentServiceBulkUpdate);
  const [services, setServices] = useState<Map<number, StudentService>>(new Map());
  const [resource, setResource] = useState<StudentServiceEditResource>({});
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [bulkUpdate] });
  const { validationErrors, setValidationErrors } = useValidationErrors();
  const [selectedItemsErrors, setSelectedItemsErrors] = useState<BulkSelectedItemErrors[]>([]);

  useEffect(() => {
    getBulkUpdate(stateParam.selectedIds);
    fetchResource();
  }, []);

  const getBulkUpdate = (ids: number[]) => {
    ApiService.studentServices.getBulkUpdate(ids).then((response) => {
      setBulkUpdate(response);
      setServices(new Map(response.services?.map((x) => [x.id, x])));
      resetUnsavedChanges([response]);
    });
  };

  const fetchResource = () => {
    ApiService.studentServices.getBulkUpdateResource().then((response) => {
      setResource(response);
    });
  };

  const save = (bulkUpdate: StudentServiceBulkUpdate) => {
    setValidationErrors({});
    setSelectedItemsErrors([]);

    ApiService.studentServices
      .saveBulkUpdate({ ...bulkUpdate, services: Array.from(services.values()) })
      .then((response) => {
        const bulkSecondLevelValidation: BulkSecondLevelValidation | undefined =
          response as unknown as BulkSecondLevelValidation;

        if (bulkSecondLevelValidation && bulkSecondLevelValidation.badRequest) {
          dispatch(setAppToast(newToast("error")));
          setSelectedItemsErrors(bulkSecondLevelValidation.selectedItemsErrors || []);
        } else {
          dispatch(setAppToast(newToast("success")));
          resetUnsavedChanges([bulkUpdate]);
          listContext.loadList?.();
          listContext.clearSelectedIds?.();
        }
      })
      .catch((ex) => {
        dispatch(setAppToast(newToast("error")));
        setValidationErrors(ex.data);
      });
  };

  return (
    <Page
      isForm
      header={
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="inherit">{studentServiceBulkUpdatePageRoute.handle}</Typography>

          <Stack direction="row" alignItems="center" spacing="sm">
            <CloseButton confirm={unsavedChanges} />

            <SubmitButton
              onClick={() => save(bulkUpdate)}
              disabled={!(bulkUpdate?.services && bulkUpdate?.services?.length > 0 && unsavedChanges)}
            />
          </Stack>
        </Stack>
      }
    >
      <Stack spacing="md">
        <ServiceUpdateSchema
          updateSchema={bulkUpdate.updateSchema || ({} as StudentServiceBulkUpdateSchema)}
          setUpdateSchema={(value) => setBulkUpdate({ ...bulkUpdate, updateSchema: value })}
          resource={resource}
          validationErrors={validationErrors?.[pn<StudentServiceBulkUpdate>("updateSchema")] || []}
        />

        <BulkSelected
          label="Service"
          items={services || []}
          setItems={(value) => setServices(value)}
          getItemLabel={(service) => (
            <Stack>
              <Typography>
                {service.student?.lastName}, {service.student?.firstName}
              </Typography>
              <Typography variant="caption" lineHeight="100%">
                {formatDate(service.date)} {service.name ? ` - ${service.name}` : ""}
              </Typography>
            </Stack>
          )}
          errors={selectedItemsErrors}
          setErrors={setSelectedItemsErrors}
        />
      </Stack>
    </Page>
  );
};
